import type {
  CollaboratorReference,
  NormalizedPayrollEmployeeRecord,
  PayrollMatch,
  PayrollMatchResult,
} from './payrollTypes';

function normalizeCpf(cpf?: string | null): string | null {
  if (!cpf) return null;
  const onlyDigits = cpf.replace(/\D/g, '');
  return onlyDigits || null;
}

function indexCollaborators(collaborators: CollaboratorReference[]) {
  const byEmployeeId = new Map<string, CollaboratorReference[]>();
  const byCpf = new Map<string, CollaboratorReference[]>();

  collaborators.forEach((c) => {
    if (c.payrollEmployeeId) {
      const list = byEmployeeId.get(c.payrollEmployeeId) ?? [];
      list.push(c);
      byEmployeeId.set(c.payrollEmployeeId, list);
    }
    const cpfNorm = normalizeCpf(c.cpf);
    if (cpfNorm) {
      const list = byCpf.get(cpfNorm) ?? [];
      list.push(c);
      byCpf.set(cpfNorm, list);
    }
  });

  return { byEmployeeId, byCpf };
}

function pickSingleOrConflict(
  candidates: CollaboratorReference[] | undefined,
): { collaborator?: CollaboratorReference; conflict: boolean } {
  if (!candidates || candidates.length === 0) {
    return { collaborator: undefined, conflict: false };
  }
  if (candidates.length > 1) {
    return { collaborator: undefined, conflict: true };
  }
  return { collaborator: candidates[0], conflict: false };
}

/**
 * Tenta casar registros de folha com colaboradores:
 * 1) employeeId salvo no colaborador
 * 2) CPF (se colaborador não tiver employeeId, mas tiver CPF igual)
 */
export function matchPayrollRecords(
  employees: NormalizedPayrollEmployeeRecord[],
  collaborators: CollaboratorReference[],
): PayrollMatchResult {
  const { byEmployeeId, byCpf } = indexCollaborators(collaborators);
  const matches: PayrollMatch[] = [];
  const conflicts: PayrollMatch[] = [];
  const unmatched: PayrollMatch[] = [];

  employees.forEach((emp) => {
    // Primeiro: employeeId
    const employeeIdMatches = byEmployeeId.get(emp.employeeId);
    const { collaborator: collById, conflict: conflictById } = pickSingleOrConflict(employeeIdMatches);
    if (conflictById) {
      const match: PayrollMatch = {
        payroll: emp,
        status: 'conflict_id',
      };
      conflicts.push(match);
      return;
    }
    if (collById) {
      matches.push({
        payroll: emp,
        collaborator: collById,
        status: 'auto_id',
        matchedBy: 'employeeId',
      });
      return;
    }

    // Segundo: CPF (apenas se colaborador não tem employeeId salvo)
    const cpfNorm = normalizeCpf(emp.cpf ?? null);
    if (cpfNorm) {
      const cpfMatches = byCpf.get(cpfNorm);
      const { collaborator: collByCpf, conflict: conflictByCpf } = pickSingleOrConflict(cpfMatches);
      if (conflictByCpf) {
        const match: PayrollMatch = {
          payroll: emp,
          status: 'conflict_cpf',
        };
        conflicts.push(match);
        return;
      }
      if (collByCpf) {
        matches.push({
          payroll: emp,
          collaborator: collByCpf,
          status: 'auto_cpf',
          matchedBy: 'cpf',
        });
        return;
      }
    }

    const pending: PayrollMatch = {
      payroll: emp,
      status: 'not_found',
    };
    unmatched.push(pending);
  });

  return { matches, conflicts, unmatched };
}
